import React, { useState, useEffect } from 'react';
import { API_BASE } from '../config';

const ENDPOINTS = [
  // VEDIC
  { category: 'Vedic', name: 'Birth Details', path: '/api/v3/vedic/birth-details', desc: 'Sublord, dignity, navamsa, vargottama' },
  { category: 'Vedic', name: 'Panchang', path: '/api/v3/vedic/panchang', desc: 'Tithi, Karana, Yoga, Nakshatra, Vaar, Hora, Rahukaal' },
  { category: 'Vedic', name: 'Manglik Dosha', path: '/api/v3/vedic/manglik-dosha', desc: 'Mars analysis with cancellation factors' },
  { category: 'Vedic', name: 'Kaal Sarpa Dosha', path: '/api/v3/vedic/kaal-sarpa-dosha', desc: '12 types of Kaal Sarpa analysis' },
  { category: 'Vedic', name: 'Sade Sati', path: '/api/v3/vedic/sade-sati', desc: "Saturn 7.5-year transit (rising/peak/setting)" },
  { category: 'Vedic', name: 'Shadbala', path: '/api/v3/vedic/shadbala', desc: '6-fold planetary strength analysis' },
  { category: 'Vedic', name: 'Ashtakvarga', path: '/api/v3/vedic/ashtakvarga', desc: 'Real bindu calculations + SAV' },
  { category: 'Vedic', name: 'Yoga Analysis', path: '/api/v3/vedic/yoga-analysis', desc: '20+ yogas (Gajakesari, Adhi, Raja, etc.)' },
  { category: 'Vedic', name: 'Divisional Chart', path: '/api/v3/vedic/divisional-chart', desc: 'Any varga D1-D60', extra: { divisor: 9 } },
  { category: 'Vedic', name: 'All Divisional Charts', path: '/api/v3/vedic/all-divisional-charts', desc: 'All 16 vargas in one call' },
  { category: 'Vedic', name: 'Transit', path: '/api/v3/vedic/transit', desc: 'Current planetary transits vs natal' },
  { category: 'Vedic', name: 'Nakshatra Predictions', path: '/api/v3/vedic/nakshatra-predictions', desc: 'Personality + career from Moon nakshatra' },
  { category: 'Vedic', name: 'Remedies', path: '/api/v3/vedic/remedies', desc: 'Per-planet mantras, gemstones, donations' },
  { category: 'Vedic', name: 'KP System', path: '/api/v3/vedic/kp-system', desc: 'Krishnamurti star/sub/sub-sub lord analysis' },
  { category: 'Vedic', name: 'Varshaphal', path: '/api/v3/vedic/varshaphal', desc: 'Annual horoscope (Tajaka system)' },
  { category: 'Vedic', name: 'Regional Panchang', path: '/api/v3/vedic/regional-panchang', desc: 'With Choghadiya, Abhijit Muhurta', flat: true },
  // WESTERN
  { category: 'Western', name: 'Natal Chart', path: '/api/v3/charts/natal', desc: 'Tropical zodiac with aspects' },
  { category: 'Western', name: 'Transit Chart', path: '/api/v3/charts/transit', desc: 'Current transits to natal positions' },
  { category: 'Western', name: 'Solar Return', path: '/api/v3/charts/solar-return', desc: 'Annual solar return chart' },
  { category: 'Western', name: 'Progressions', path: '/api/v3/charts/progressions', desc: 'Secondary progressed chart' },
  { category: 'Western', name: 'Aspects', path: '/api/v3/data/aspects', desc: 'Major + minor planetary aspects' },
  // HOROSCOPE
  { category: 'Horoscope', name: 'Daily (by sign)', path: '/api/v3/horoscope/sign/daily', desc: 'AI-generated daily horoscope', special: 'sign' },
  { category: 'Horoscope', name: 'Weekly (by sign)', path: '/api/v3/horoscope/sign/weekly', desc: 'AI-generated weekly horoscope', special: 'sign' },
  { category: 'Horoscope', name: 'Monthly (by sign)', path: '/api/v3/horoscope/sign/monthly', desc: 'AI-generated monthly horoscope', special: 'sign' },
  { category: 'Horoscope', name: 'Yearly (by sign)', path: '/api/v3/horoscope/sign/yearly', desc: 'AI-generated yearly horoscope', special: 'sign' },
  { category: 'Horoscope', name: 'Daily (personal)', path: '/api/v3/horoscope/personal/daily', desc: 'Personalized to your chart' },
  // BILLING
  { category: 'Billing', name: 'Pricing Tiers', path: '/api/v3/billing/tiers', desc: 'Get available subscription tiers', method: 'GET' }
];

// Rate limiting: 10 requests per 5 minutes (humble for testing)
const RATE_LIMIT = { max: 10, windowMs: 5 * 60 * 1000 };
const RATE_KEY = 'astrogem_playground_usage';

function ApiPlayground({ onBack }) {
  // Birth details form
  const [birth, setBirth] = useState({
    name: 'Test User',
    city: 'New Delhi',
    date: '1981-08-19',
    time: '20:20',
    latitude: 28.6139,
    longitude: 77.2090,
    sign: 'Pisces'
  });
  const [selected, setSelected] = useState(ENDPOINTS[0]);
  const [request, setRequest] = useState('');
  const [response, setResponse] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Vedic');
  const [rateLimit, setRateLimit] = useState(RATE_LIMIT.max);

  const categories = ['Vedic', 'Western', 'Horoscope', 'Billing'];
  const filteredEndpoints = ENDPOINTS.filter(e => e.category === activeCategory);

  // Build request body from birth + endpoint config
  const buildBody = (ep, b) => {
    if (ep.method === 'GET') return null;
    if (ep.special === 'sign') return { sign: b.sign, date: new Date().toISOString().split('T')[0] };
    if (ep.flat) {
      const [year, month, day] = b.date.split('-').map(Number);
      const [hour, minute] = b.time.split(':').map(Number);
      return { year, month, day, hour, minute, latitude: b.latitude, longitude: b.longitude, region: 'north_india' };
    }
    const [year, month, day] = b.date.split('-').map(Number);
    const [hour, minute] = b.time.split(':').map(Number);
    const body = {
      subject: {
        name: b.name,
        birth_data: { year, month, day, hour, minute, second: 0, latitude: parseFloat(b.latitude), longitude: parseFloat(b.longitude) }
      },
      options: { ayanamsa: 'lahiri' }
    };
    if (ep.extra) body.options = { ...body.options, ...ep.extra };
    return body;
  };

  // Update request when birth or endpoint changes
  useEffect(() => {
    const body = buildBody(selected, birth);
    setRequest(body ? JSON.stringify(body, null, 2) : '(GET request - no body needed)');
  }, [birth, selected]);

  // Load rate limit from localStorage
  useEffect(() => {
    const updateLimit = () => {
      const stored = JSON.parse(localStorage.getItem(RATE_KEY) || '{"calls":[]}');
      const now = Date.now();
      const recentCalls = (stored.calls || []).filter(t => now - t < RATE_LIMIT.windowMs);
      setRateLimit(Math.max(0, RATE_LIMIT.max - recentCalls.length));
    };
    updateLimit();
    const interval = setInterval(updateLimit, 5000);
    return () => clearInterval(interval);
  }, []);

  const selectEndpoint = (ep) => {
    setSelected(ep);
    setResponse(null);
  };

  const checkRateLimit = () => {
    const stored = JSON.parse(localStorage.getItem(RATE_KEY) || '{"calls":[]}');
    const now = Date.now();
    const recentCalls = (stored.calls || []).filter(t => now - t < RATE_LIMIT.windowMs);
    return recentCalls.length < RATE_LIMIT.max;
  };

  const recordCall = () => {
    const stored = JSON.parse(localStorage.getItem(RATE_KEY) || '{"calls":[]}');
    const now = Date.now();
    const recentCalls = (stored.calls || []).filter(t => now - t < RATE_LIMIT.windowMs);
    recentCalls.push(now);
    localStorage.setItem(RATE_KEY, JSON.stringify({ calls: recentCalls }));
    setRateLimit(Math.max(0, RATE_LIMIT.max - recentCalls.length));
  };

  const tryEndpoint = async () => {
    if (!checkRateLimit()) {
      setResponse({ error: `Rate limit reached. Please wait ${RATE_LIMIT.windowMs/60000} min before testing more. Sign up for unlimited access.` });
      return;
    }

    setLoading(true);
    setResponse(null);
    try {
      const opts = {
        method: selected.method || 'POST',
        headers: { 'Content-Type': 'application/json' }
      };
      if (selected.method !== 'GET') opts.body = request;

      const startTime = performance.now();
      const res = await fetch(`${API_BASE}${selected.path}`, opts);
      const elapsed = performance.now() - startTime;
      const data = await res.json();

      setResponse({ status: res.status, elapsed: Math.round(elapsed), data });
      recordCall();
    } catch (e) {
      setResponse({ error: e.message });
    } finally {
      setLoading(false);
    }
  };

  // Quick city presets
  const cityPresets = [
    { name: 'New Delhi, India', lat: 28.6139, lon: 77.2090 },
    { name: 'Mumbai, India', lat: 19.0760, lon: 72.8777 },
    { name: 'Bangalore, India', lat: 12.9716, lon: 77.5946 },
    { name: 'New York, USA', lat: 40.7128, lon: -74.0060 },
    { name: 'London, UK', lat: 51.5074, lon: -0.1278 },
    { name: 'Tokyo, Japan', lat: 35.6762, lon: 139.6503 }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 text-white">
      {/* Header */}
      <div className="border-b border-slate-800 px-6 py-4 sticky top-0 bg-slate-900/95 backdrop-blur z-10">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-3">
          <button onClick={onBack} className="text-white/70 hover:text-white text-sm">← Back to Home</button>
          <h1 className="text-xl font-bold">📡 API Playground</h1>
          <div className="flex items-center gap-3 text-xs">
            <span className="text-white/50">{ENDPOINTS.length} endpoints</span>
            <span className={`px-3 py-1 rounded font-semibold ${rateLimit > 5 ? 'bg-green-500/20 text-green-300' : rateLimit > 0 ? 'bg-yellow-500/20 text-yellow-300' : 'bg-red-500/20 text-red-300'}`}>
              {rateLimit}/{RATE_LIMIT.max} test calls remaining
            </span>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {/* BIRTH DETAILS FORM */}
        <div className="mb-6 bg-slate-800/50 border border-slate-700 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold">📅 Birth Details (used for all tests)</h2>
            <span className="text-xs text-white/50">Edit these to test different charts</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="block text-xs text-white/60 mb-1">Name</label>
              <input value={birth.name} onChange={(e) => setBirth({...birth, name: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Date</label>
              <input type="date" value={birth.date} onChange={(e) => setBirth({...birth, date: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Time (24h)</label>
              <input type="time" value={birth.time} onChange={(e) => setBirth({...birth, time: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">City Preset</label>
              <select onChange={(e) => {
                const c = cityPresets[parseInt(e.target.value)];
                if (c) setBirth({...birth, city: c.name, latitude: c.lat, longitude: c.lon});
              }}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400">
                <option>{birth.city}</option>
                {cityPresets.map((c, i) => <option key={i} value={i}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Latitude</label>
              <input type="number" step="0.0001" value={birth.latitude} onChange={(e) => setBirth({...birth, latitude: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400" />
            </div>
            <div>
              <label className="block text-xs text-white/60 mb-1">Longitude</label>
              <input type="number" step="0.0001" value={birth.longitude} onChange={(e) => setBirth({...birth, longitude: e.target.value})}
                className="w-full px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm focus:outline-none focus:border-purple-400" />
            </div>
          </div>
          {/* Sign selector for horoscope endpoints */}
          {selected.special === 'sign' && (
            <div className="mt-3">
              <label className="block text-xs text-white/60 mb-1">Sign (for horoscope endpoints)</label>
              <select value={birth.sign} onChange={(e) => setBirth({...birth, sign: e.target.value})}
                className="px-3 py-2 bg-slate-900 border border-slate-700 rounded text-sm">
                {['Aries','Taurus','Gemini','Cancer','Leo','Virgo','Libra','Scorpio','Sagittarius','Capricorn','Aquarius','Pisces'].map(s =>
                  <option key={s}>{s}</option>
                )}
              </select>
            </div>
          )}
        </div>

        {/* Category Tabs */}
        <div className="flex gap-2 mb-6 border-b border-slate-700 overflow-x-auto">
          {categories.map(cat => (
            <button key={cat}
              onClick={() => setActiveCategory(cat)}
              className={`px-6 py-3 text-sm font-semibold transition border-b-2 ${
                activeCategory === cat ? 'text-purple-400 border-purple-400' : 'text-white/60 border-transparent hover:text-white'
              }`}>
              {cat} ({ENDPOINTS.filter(e => e.category === cat).length})
            </button>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* LEFT: Endpoint List */}
          <div className="space-y-2 lg:col-span-1">
            <h3 className="text-sm font-bold text-white/80 mb-3">Endpoints</h3>
            <div className="space-y-1 max-h-[70vh] overflow-y-auto">
              {filteredEndpoints.map(ep => (
                <button key={ep.path}
                  onClick={() => selectEndpoint(ep)}
                  className={`w-full text-left px-4 py-3 rounded-lg border transition ${
                    selected.path === ep.path
                      ? 'bg-purple-500/20 border-purple-400'
                      : 'bg-slate-800/50 border-slate-700 hover:bg-slate-800'
                  }`}>
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs px-2 py-0.5 bg-slate-700 rounded">{ep.method || 'POST'}</span>
                    <span className="font-semibold text-sm">{ep.name}</span>
                  </div>
                  <p className="text-xs text-white/50">{ep.desc}</p>
                  <p className="text-[10px] text-white/30 mt-1 font-mono">{ep.path}</p>
                </button>
              ))}
            </div>
          </div>

          {/* MIDDLE: Request */}
          <div className="lg:col-span-1">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-white/80">Request</h3>
              <button onClick={tryEndpoint} disabled={loading}
                className="px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600 rounded-lg text-sm font-bold disabled:opacity-50">
                {loading ? '⏳ Calling...' : '▶ Try It'}
              </button>
            </div>
            <div className="mb-2 text-xs text-white/50">
              <code className="bg-slate-800 px-2 py-1 rounded">{selected.method || 'POST'} {selected.path}</code>
            </div>
            <textarea value={request} onChange={(e) => setRequest(e.target.value)}
              className="w-full h-[60vh] bg-slate-900 border border-slate-700 rounded-lg p-4 text-sm font-mono text-green-300 focus:outline-none focus:border-purple-400"
              spellCheck={false} />
          </div>

          {/* RIGHT: Response */}
          <div className="lg:col-span-1">
            <h3 className="text-sm font-bold text-white/80 mb-3">Response</h3>
            <div className="bg-slate-900 border border-slate-700 rounded-lg p-4 h-[60vh] overflow-y-auto">
              {!response && (
                <div className="text-white/40 text-center py-12">
                  <div className="text-4xl mb-3">📡</div>
                  <p>Click "Try It" to see live response</p>
                </div>
              )}
              {response?.error && (
                <div className="text-red-400">
                  <p className="font-semibold mb-2">❌ Error</p>
                  <p className="text-sm">{response.error}</p>
                </div>
              )}
              {response?.data && (
                <>
                  <div className="flex gap-3 mb-3 text-xs">
                    <span className={`px-2 py-1 rounded ${response.status === 200 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'}`}>
                      HTTP {response.status}
                    </span>
                    <span className="px-2 py-1 bg-slate-700 rounded text-white/60">⏱️ {response.elapsed}ms</span>
                  </div>
                  <pre className="text-xs text-white/80 font-mono whitespace-pre-wrap break-all">
                    {JSON.stringify(response.data, null, 2)}
                  </pre>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Documentation Block */}
        <div className="mt-8 p-6 bg-slate-800/50 border border-slate-700 rounded-xl">
          <h3 className="text-lg font-bold mb-3">📋 How to use this endpoint</h3>
          <div className="space-y-3 text-sm">
            <div>
              <p className="text-white/60 mb-1">Endpoint:</p>
              <code className="bg-slate-900 px-3 py-2 rounded block font-mono text-purple-300">
                {selected.method || 'POST'} https://astrogemapi.com{selected.path}
              </code>
            </div>
            <div>
              <p className="text-white/60 mb-1">Authentication (in production):</p>
              <code className="bg-slate-900 px-3 py-2 rounded block font-mono text-purple-300">
                Authorization: Bearer YOUR_API_KEY
              </code>
            </div>
            <div>
              <p className="text-white/60 mb-1">cURL example:</p>
              <code className="bg-slate-900 px-3 py-2 rounded block font-mono text-green-300 text-xs whitespace-pre">
{`curl -X ${selected.method || 'POST'} https://astrogemapi.com${selected.path} \\
  -H "Authorization: Bearer YOUR_API_KEY" \\
  -H "Content-Type: application/json"${selected.method !== 'GET' ? ` \\
  -d '${request.replace(/\n\s*/g, ' ').substring(0, 100)}...'` : ''}`}
              </code>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ApiPlayground;
